import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { FOOTER } from "@/constants/testIds";

const footerLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Assessments", path: "/assessments" },
  { name: "FAQ", path: "/faq" },
  { name: "Contact", path: "/contact" },
];

export const Footer = () => {
  return (
    <footer data-testid={FOOTER.section} className="glass-footer">
      {/* Main Footer Content */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div data-testid={FOOTER.brand}>
              <h3 className="font-heading text-xl font-bold text-white mb-1">
                MODERN MINDS
              </h3>
              <p className="font-body text-[10px] text-[#7C9885] tracking-wider uppercase mb-3">
                Center for Child Psychology; Behavioural Health & Parenting Consultancy
              </p>
              <p className="font-body text-sm text-[#94A3B8] leading-relaxed">
                Bridging Global Brain Science with Human Connection.
              </p>
            </div>

            {/* Quick Links */}
            <div data-testid={FOOTER.links}>
              <h4 className="font-body font-semibold text-white mb-3 text-sm">Navigation</h4>
              <ul className="space-y-2">
                {footerLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="font-body text-[#94A3B8] hover:text-[#7C9885] transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-body font-semibold text-white mb-3 text-sm">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <MapPin size={16} className="text-[#7C9885] mt-0.5 flex-shrink-0" />
                  <span className="font-body text-[#94A3B8] text-sm">
                    1/4 A Vineet Khand, Gomti Nagar<br />
                    Lucknow, UP 226010, India
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone size={16} className="text-[#7C9885] flex-shrink-0" />
                  <a href="tel:+919569170611" className="font-body text-[#94A3B8] text-sm hover:text-[#7C9885] transition-colors">
                    +91 9569 170 611
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail size={16} className="text-[#7C9885] flex-shrink-0" />
                  <a href="mailto:isha@modernminds.co.in" className="font-body text-[#94A3B8] text-sm hover:text-[#7C9885] transition-colors">
                    isha@modernminds.co.in
                  </a>
                </li>
              </ul>
            </div>

            {/* Hours & QR */}
            <div>
              <h4 className="font-body font-semibold text-white mb-3 text-sm">Clinical Hours</h4>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <Clock size={16} className="text-[#7C9885] mt-0.5 flex-shrink-0" />
                  <div className="font-body text-[#94A3B8] text-sm">
                    <p>Mon–Fri: 9:00 AM – 6:00 PM</p>
                    <p>Saturday: 10:00 AM – 2:00 PM</p>
                    <p>Sunday: Closed</p>
                  </div>
                </li>
              </ul>
              
              {/* QR Code - Instagram */}
              <div className="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/isharastogipsychology/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-2 bg-white rounded-lg hover:scale-105 transition-transform"
                  aria-label="Scan QR code or click to visit Instagram"
                >
                  <QRCodeSVG
                    value="https://www.instagram.com/isharastogipsychology/"
                    size={80}
                    level="M"
                    bgColor="#FFFFFF"
                    fgColor="#0F172A"
                  />
                </a>
                <div>
                  <a
                    href="https://www.instagram.com/isharastogipsychology/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#7C9885] hover:text-white transition-colors font-body text-sm font-medium mb-1"
                  >
                    <Instagram size={16} />
                    @isharastogipsychology
                  </a>
                  <p className="font-body text-[10px] text-[#94A3B8]">Scan to connect</p>
                </div>
              </div>
              <a
                href="https://consult.modernminds.co.in"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold rounded px-4 py-2 font-body font-semibold text-xs mt-4 inline-block"
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Disclaimer - Mandatory 34-word statutory shield */}
      <div className="legal-disclaimer py-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-body text-xs text-[#94A3B8] leading-relaxed text-center">
            This Biopsychosocial Mapping Profile is generated strictly for clinical case formulation, therapeutic tailoring, and parental insight. This document does not constitute a statutory Medico-Legal Disability Certificate under the RPwD Act (2016) or RCI Act (1992).
          </p>
          <p className="font-body text-xs text-[#64748B] text-center mt-3">
            © {new Date().getFullYear()} MODERN MINDS. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
